import React from 'react';
import { cn } from '@/lib/utils';
import { selectionItemOutlineClasses } from '@/lib/selection-item-styles';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslation } from 'react-i18next';
import { dealClosingDayIso, closingDateDisplayClass } from '@/lib/crm-closing-date';

export type Deal = {
  id: string;
  title: string;
  ownerEmail: string;
  value?: number | null;
  currency?: string | null;
  closingDate?: string | null;
  address?: string | null;
  city?: string | null;
  tags?: string[];
  productSummary?: string | null;
  measurementData?: { products?: Array<{ productType: string; index: number }> } | null;
  measurement_data?: { products?: Array<{ productType: string; index: number }> } | null;
  facebookLeadgenId?: string | null;
  facebookDataIncomplete?: boolean | null;
  facebookQuoteQuality?: string | null;
};

function getProductSummary(deal: Deal): string | null {
  const products = deal.measurementData?.products ?? deal.measurement_data?.products;
  if (!products?.length) return null;
  const counts = new Map<string, number>();
  products.forEach((p) => {
    const key = p.productType;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([name, n]) => (n > 1 ? `${n}× ${name}` : name))
    .join(", ");
}

export default function DealCard({ 
  deal, 
  onOpen,
  selected,
  onSelect,
  selectionMode = false,
}: { 
  deal: Deal; 
  onOpen?: (id: string) => void;
  selected?: boolean;
  onSelect?: (id: string, checked: boolean) => void;
  selectionMode?: boolean;
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const fb = deal.facebookLeadgenId;
  const amount = deal.value != null ? `${deal.value.toLocaleString()} ${deal.currency || ''}`.trim() : null;
  const closingDay = dealClosingDayIso(deal);
  const date = closingDay
    ? new Date(closingDay + 'T00:00:00').toLocaleDateString()
    : null;
  const addressLine = [deal.address?.trim(), deal.city?.trim()].filter(Boolean).join(', ');
  const productSummary = deal.productSummary ?? getProductSummary(deal);

  const handleClick = (e: React.MouseEvent) => {
    // If selection mode is enabled, toggle selection instead of opening the deal
    if (selectionMode && onSelect) {
      const isSelected = selected || false;
      onSelect(deal.id, !isSelected);
    } else {
      // In normal mode, open the deal
      onOpen?.(deal.id);
    }
  };

  return (
    <div
      draggable={!selectionMode && !isMobile}
      onDragStart={(e) => {
        if (!selectionMode && !isMobile) {
          e.dataTransfer.setData('text/dealId', deal.id);
        }
      }}
      onClick={handleClick}
      className={cn(
        'rounded-lg border border-border bg-card shadow-sm hover:shadow-lg hover:bg-accent/50 transition-all duration-200 ease-out relative cursor-pointer',
        isMobile ? 'p-3.5' : 'p-4 hover:-translate-y-0.5',
        selectionMode ? 'cursor-pointer' : 'cursor-pointer active:cursor-grabbing',
        selectionItemOutlineClasses(Boolean(selectionMode), Boolean(selected))
      )}
    >
      <div className={cn('flex items-start gap-2', isMobile && 'gap-3')}>
        <div className="flex-1 min-w-0">
          {/* Mobil: nagyobb betűk és helyközök (~5 deal látszódjon), asztali változatlan */}
          <p className={cn(
            'text-sm font-normal text-foreground/90 dark:text-foreground truncate pr-2',
            isMobile ? 'leading-normal' : 'leading-snug'
          )} title={deal.title}>{deal.title}</p>
          {amount && <p className={cn(
            'text-sm font-light text-foreground/80 dark:text-foreground/90',
            isMobile ? 'mt-1' : 'mt-1'
          )}>{amount}</p>}
          {date && (
            <p className={cn(
              'text-sm font-light',
              closingDateDisplayClass(closingDay),
              isMobile ? 'mt-1' : 'mt-1'
            )}>
              {isMobile ? 'Zárás: ' : ''}{date}
            </p>
          )}
          {addressLine ? (
            <p
              className={cn(
                'font-light text-foreground/65 dark:text-muted-foreground truncate',
                isMobile ? 'text-xs mt-1' : 'text-xs mt-0.5'
              )}
              title={addressLine}
            >
              {addressLine}
            </p>
          ) : null}
          {isMobile && fb && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {deal.facebookDataIncomplete && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-900">
                  {t('crm.facebookIncomplete')}
                </span>
              )}
              {deal.facebookQuoteQuality === 'placeholder' && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-200 text-slate-800">
                  {t('crm.facebookPlaceholderQuote')}
                </span>
              )}
              {deal.facebookQuoteQuality === 'failed' && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-800">
                  {t('crm.facebookQuoteFailed')}
                </span>
              )}
              {deal.facebookQuoteQuality === 'skipped_size' && (
                <span
                  className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  title={t('crm.facebookSkippedSizeHint')}
                >
                  {t('crm.facebookSkippedSizeQuote')}
                </span>
              )}
            </div>
          )}
          {!isMobile && productSummary && (
            <p className="text-xs font-light text-foreground/65 dark:text-muted-foreground mt-1 truncate" title={productSummary}>{productSummary}</p>
          )}
          {!isMobile && (
            <div className="mt-2 flex flex-wrap gap-1 items-center">
              {fb && deal.facebookDataIncomplete && (
                <span
                  className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200"
                  title={t('crm.facebookIncompleteHint')}
                >
                  {t('crm.facebookIncomplete')}
                </span>
              )}
              {fb && deal.facebookQuoteQuality === 'placeholder' && (
                <span
                  className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100"
                  title={t('crm.facebookPlaceholderHint')}
                >
                  {t('crm.facebookPlaceholderQuote')}
                </span>
              )}
              {fb && deal.facebookQuoteQuality === 'failed' && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-200">
                  {t('crm.facebookQuoteFailed')}
                </span>
              )}
              {fb && deal.facebookQuoteQuality === 'skipped_size' && (
                <span
                  className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100"
                  title={t('crm.facebookSkippedSizeHint')}
                >
                  {t('crm.facebookSkippedSizeQuote')}
                </span>
              )}
              {(deal.tags || []).slice(0,3).map((tag) => (
                <span key={tag} className="text-[10px] font-light px-1.5 py-0.5 rounded bg-muted/70 text-foreground/65 dark:text-muted-foreground">{tag}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
